import React, { useEffect } from "react";
import { SignIn, useClerk } from "@clerk/clerk-react";
import { useCommonTranslation } from "@tablemint/i18n";
import { motion } from "framer-motion";
import AnimatedSection from "../components/animations/AnimatedSection";

const Login: React.FC = () => {
  const { t } = useCommonTranslation();
  const clerk = useClerk();

  useEffect(() => {
    console.log("Clerk instance:", clerk);
    console.log("Current user status:", clerk.user);
    console.log("Session:", clerk.session);
  }, [clerk]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
        {/* Left side - Brand message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:flex flex-col items-center justify-center p-8 bg-gradient-to-br from-primary/10 to-primary/5 text-foreground relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-pattern opacity-5"></div>

          <div className="relative z-10 max-w-md text-center px-4">
            <h2 className="text-4xl font-bold mb-6 text-primary">
              {t("app.name")}
            </h2>
            <p className="text-xl mb-8">{t("app.tagline")}</p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center p-4 bg-card rounded-lg shadow-sm">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="font-medium">{t("auth.benefitTitle1")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.benefitDesc1")}
                  </p>
                </div>
              </div>

              <div className="flex items-center p-4 bg-card rounded-lg shadow-sm">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="font-medium">{t("auth.benefitTitle2")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("auth.benefitDesc2")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Right side - Clerk component */}
        <AnimatedSection className="flex items-center justify-center p-4 sm:p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("auth.login")}
              </h1>
              <p className="text-muted-foreground">
                {t("auth.loginInstruction")}
              </p>
            </div>

            <div className="bg-card rounded-xl shadow-lg p-6 sm:p-8 border border-border/50">
              <SignIn
                routing="path"
                path="/sign-in"
                signUpUrl="/sign-up"
                appearance={{
                  elements: {
                    formButtonPrimary:
                      "bg-primary hover:bg-primary/90 text-white",
                    card: "bg-transparent shadow-none",
                    footerActionLink: "text-primary hover:text-primary/90",
                    headerTitle: "hidden",
                    headerSubtitle: "hidden",
                  },
                }}
                fallbackRedirectUrl="/dashboard"
              />
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-muted-foreground">
                {t("auth.noAccount")}{" "}
                <a href="/sign-up" className="text-primary hover:underline">
                  {t("auth.signupHere")}
                </a>
              </p>
            </div>

            <div className="text-center mt-4">
              <p className="text-xs text-muted-foreground mb-2">
                {t("auth.debugInfo")}
              </p>
              <pre className="bg-muted p-2 rounded text-xs text-left overflow-auto max-h-40">
                {JSON.stringify(
                  {
                    isLoaded: clerk.loaded,
                    user: clerk.user ? "User exists" : "No user",
                    session: clerk.session ? "Session exists" : "No session",
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
};

export default Login;
